import React, { useEffect, useState } from "react";

import { Stack, Tooltip, Typography } from "@mui/material";
import NetworkCheckIcon from "@mui/icons-material/NetworkCheck";

import { useMeetingManager } from "amazon-chime-sdk-component-library-react";

/**
 * Props for the CallQuality component
 */
interface CallQualityProps {
  /** Whether to show the text label alongside the icon */
  showLabel?: boolean;
  /** Whether to wait for call quality calculation before showing results */
  waitForCallQualityCalculation?: boolean;
}

/**
 * Color values for call quality indicators
 */
enum CallColor {
  red = "Red",
  orange = "Orange",
  green = "Green",
}

/**
 * Quality levels for call assessment
 */
enum CallQualityLevel {
  good = "Good",
  bad = "Bad",
  ok = "Ok",
}

/**
 * CallQuality component displays real-time network quality information
 * for Amazon Chime SDK meetings, showing bandwidth and connection status.
 */
const CallQuality: React.FC<CallQualityProps> = ({
  showLabel,
  waitForCallQualityCalculation = false,
}) => {
  const meetingManager = useMeetingManager();

  // State for call quality metrics
  const [statString, setStatString] = useState<CallQualityLevel>(
    CallQualityLevel.good
  );
  const [callColour, setCallColour] = useState<CallColor>(CallColor.green);
  const [videoWarning, setVideoWarning] = useState<boolean>(false);
  const [bandwidth, setBandwidth] = useState<number | null>(null);
  const [observerSet, setObserverSet] = useState(false);
  const [firstRenderTime, setFirstRenderTime] = useState<number>(Date.now());

  // Bandwidth thresholds (in kbps)
  const BANDWIDTH_THRESHOLDS = {
    minVideo: 600,        // Minimum for video calls
    okQuality: 1000,      // Threshold for "ok" quality
    badQuality: 400,      // Threshold for "bad" quality
  };

  // Determine if we should wait for bandwidth calculation
  const shouldWaitForCalculation =
    bandwidth == null ||
    (waitForCallQualityCalculation && firstRenderTime + 20000 > Date.now());

  // Set up metrics observer to monitor call quality
  useEffect(() => {
    if (observerSet || !meetingManager.audioVideo) {
      return;
    }

    const observerMetrics = {
      metricsDidReceive: (clientMetricReport: {
        getObservableMetrics: () => any;
      }) => {
        const metricReport = clientMetricReport.getObservableMetrics();

        const {
          availableOutgoingBitrate,
          availableIncomingBitrate,
          videoUpstreamBitrate,
          videoPacketSentPerSecond,
        } = metricReport;

        // Convert to kbps for easier threshold comparison
        const outgoingBandwidth = availableOutgoingBitrate / 1000;

        // Determine quality level based on bandwidth thresholds
        if (outgoingBandwidth < BANDWIDTH_THRESHOLDS.badQuality) {
          setStatString(CallQualityLevel.bad);
          setCallColour(CallColor.red);
        } else if (outgoingBandwidth < BANDWIDTH_THRESHOLDS.okQuality) {
          setStatString(CallQualityLevel.ok);
          setCallColour(CallColor.orange);
        } else {
          setStatString(CallQualityLevel.good);
          setCallColour(CallColor.green);
        }

        // Check if video quality might be affected
        const videoBad = outgoingBandwidth < BANDWIDTH_THRESHOLDS.minVideo;
        setVideoWarning(videoBad);
        setBandwidth(outgoingBandwidth);
      },
    };

    meetingManager.audioVideo.addObserver(observerMetrics);
    setObserverSet(true);
  }, [meetingManager.audioVideo]);

  // Calculate call quality ratio as a percentage (0-100%)
  let callRatio = bandwidth ? bandwidth / BANDWIDTH_THRESHOLDS.okQuality : 0;

  // Ensure callRatio is within valid bounds
  if (Number.isNaN(callRatio) || callRatio == null || callRatio < 0) {
    callRatio = 0;
  } else if (callRatio > 1) {
    callRatio = 1;
  }

  callRatio *= 100;

  return (
    <Tooltip
      title={
        !shouldWaitForCalculation && bandwidth
          ? videoWarning
            ? "Bandwidth too low - video may be disabled or degraded."
            : `Bandwidth: ${bandwidth.toFixed(0)} kbps (${callRatio.toFixed()}%)`
          : "Calculating outgoing bandwidth..."
      }
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          backgroundColor:
            !shouldWaitForCalculation && bandwidth
              ? callColour === CallColor.green
                ? "#c8e6c950"    // Light green for good quality
                : callColour === CallColor.orange
                ? "#ffcc8050"    // Light orange for ok quality
                : "#ffab9150"    // Light red for bad quality
              : "#e0e0e050",     // Grey while calculating
          padding: 1,
          borderRadius: 1,
          cursor: "pointer",
        }}
      >
        <NetworkCheckIcon
          style={{
            color: !shouldWaitForCalculation && bandwidth ? callColour : "grey",
          }}
        />
        {showLabel && (
          <Typography
            variant="h6"
            sx={{ color: "black" }}
          >
            {!shouldWaitForCalculation && bandwidth
              ? statString
              : "Calculating..."}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
};

export default CallQuality;
